import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, Instagram, ArrowRight } from "lucide-react";
import { postNewsletter } from "../api";
import { useToast } from "../hooks/use-toast";
import { useI18n } from "../i18n/I18nContext";
import { socialLinks } from "../mock";

const footerLinks = {
  colExplore: [
    { key: "insights", path: "/insights" },
    { key: "capabilities", path: "/capabilities" },
    { key: "magazine", path: "/magazine" },
  ],
  colCompany: [
    { key: "about", path: "/about" },
    { key: "contact", path: "/contact" },
    { key: "careers", path: "/about" },
  ],
  colCapabilities: [
    { key: "capMS", path: "/capabilities", term: "Marketing Strategy" },
    { key: "capBD", path: "/capabilities", term: "Business Development" },
    { key: "capDA", path: "/capabilities", term: "Data & Analytics" },
  ],
};

const Footer = () => {
  const { toast } = useToast();
  const { t, term } = useI18n();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await postNewsletter(email);
      toast({ title: t("subscribedTitle"), description: t("subscribedBody") });
      setEmail("");
    } catch (err) {
      toast({ title: t("subscribeFailTitle"), description: t("subscribeFailBody") });
    }
  };

  return (
    <footer className="bg-[#0a2540] text-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 mb-5">
              <div className="flex items-center justify-center w-9 h-9 rounded-md bg-white text-[#0a2540] font-display font-800 text-lg">
                B
              </div>
              <span className="font-display font-800 text-xl">BPC</span>
            </div>
            <p className="text-slate-300 text-[15px] leading-relaxed max-w-sm">
              {t("footerTagline")}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Linkedin, label: "LinkedIn", url: socialLinks.linkedin },
                { Icon: Instagram, label: "Instagram", url: socialLinks.instagram },
                { Icon: Twitter, label: "X", url: socialLinks.twitter },
                { Icon: Youtube, label: "YouTube", url: socialLinks.youtube },
              ].map(({ Icon, label, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-[#0a6ed1] transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {Object.entries(footerLinks).map(([titleKey, links]) => (
              <div key={titleKey}>
                <h4 className="text-sm font-600 text-slate-400 uppercase tracking-wider mb-4">
                  {t(titleKey)}
                </h4>
                <ul className="space-y-3">
                  {links.map((l) => (
                    <li key={l.key}>
                      <Link
                        to={l.path}
                        className="text-[15px] text-slate-200 hover:text-white transition-colors"
                      >
                        {l.term ? term(l.term) : t(l.key)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-600 text-slate-400 uppercase tracking-wider mb-4">
              {t("colNewsletter")}
            </h4>
            <p className="text-slate-300 text-[15px] mb-4">
              {t("newsletterBody")}
            </p>
            <form
              onSubmit={subscribe}
              className="flex items-center bg-white/10 rounded-full overflow-hidden"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                className="bg-transparent px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none flex-1"
              />
              <button type="submit" className="flex items-center justify-center w-11 h-11 bg-[#0a6ed1] hover:bg-[#0857a8] transition-colors rounded-full m-1">
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

        <div className="py-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} BPC — Business Performance Consulting. {t("rights")}
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/impressum" className="hover:text-white transition-colors" data-testid="footer-impressum-link">Impressum</Link>
            <a href="#" className="hover:text-white transition-colors">{t("privacy")}</a>
            <a href="#" className="hover:text-white transition-colors">{t("terms")}</a>
            <a href="#" className="hover:text-white transition-colors">{t("cookies")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
